import {
  type DocumentData,
  type QueryConstraint,
  type WithFieldValue,
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  updateDoc,
} from 'firebase/firestore';

import { getFirebaseDb } from './app';

export async function getDocument<T extends DocumentData>(
  collectionPath: string,
  docId: string,
): Promise<(T & { id: string }) | null> {
  const db = getFirebaseDb();
  const docRef = doc(db, collectionPath, docId);
  const snapshot = await getDoc(docRef);

  if (!snapshot.exists()) {
    return null;
  }

  return { id: snapshot.id, ...snapshot.data() } as T & { id: string };
}

export async function getDocuments<T extends DocumentData>(
  collectionPath: string,
  ...constraints: QueryConstraint[]
): Promise<(T & { id: string })[]> {
  const db = getFirebaseDb();
  const q = query(collection(db, collectionPath), ...constraints);
  const snapshot = await getDocs(q);

  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }) as T & { id: string });
}

export async function addDocument<T extends DocumentData>(
  collectionPath: string,
  data: WithFieldValue<T>,
): Promise<string> {
  const db = getFirebaseDb();
  const docRef = await addDoc(collection(db, collectionPath), data);
  return docRef.id;
}

export async function updateDocument(
  collectionPath: string,
  docId: string,
  data: Record<string, unknown>,
): Promise<void> {
  const db = getFirebaseDb();
  const docRef = doc(db, collectionPath, docId);
  await updateDoc(docRef, data);
}

export async function setDocument(
  collectionPath: string,
  docId: string,
  data: Record<string, unknown>,
): Promise<void> {
  const db = getFirebaseDb();
  const docRef = doc(db, collectionPath, docId);
  await setDoc(docRef, data);
}

export async function deleteDocument(collectionPath: string, docId: string): Promise<void> {
  const db = getFirebaseDb();
  const docRef = doc(db, collectionPath, docId);
  await deleteDoc(docRef);
}

export function subscribeToDocument<T extends DocumentData>(
  collectionPath: string,
  docId: string,
  callback: (data: (T & { id: string }) | null) => void,
): () => void {
  const db = getFirebaseDb();
  const docRef = doc(db, collectionPath, docId);

  return onSnapshot(docRef, (snapshot) => {
    if (!snapshot.exists()) {
      callback(null);
      return;
    }
    callback({ id: snapshot.id, ...snapshot.data() } as T & { id: string });
  });
}

export function subscribeToCollection<T extends DocumentData>(
  collectionPath: string,
  constraints: QueryConstraint[],
  callback: (data: (T & { id: string })[]) => void,
): () => void {
  const db = getFirebaseDb();
  const q = query(collection(db, collectionPath), ...constraints);

  return onSnapshot(q, (snapshot) => {
    const results = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }) as T & { id: string });
    callback(results);
  });
}
